import { UseQueryResult } from "@tanstack/react-query";
import { fetcher } from "@website/fetcher/fetcher";
import { useFetcher } from "@website/fetcher/use-fetcher";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { AUTH_ENDPOINTS } from "./constants";
import {
  AuthResponse,
  B2BProfile,
  UserInfo,
  UserInfoUpdateBody
} from "./types";
import { saveLogin, useJwtToken, useLoginStatus } from "./utils";

export const convert = (token: string) =>
  fetcher<AuthResponse>({
    url: AUTH_ENDPOINTS.CONVERT,
    method: "POST",
    data: { snappToken: token }
  }).then(({ data }) => {
    saveLogin(data);
    addEventToTracking(USER_TRACKING_EVENTS.JEK_LOGIN, {
      product: "core"
    });
  });

export const useProfile = () => {
  const { profileType } = useJwtToken();
  const { isLoggedIn } = useLoginStatus();

  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;

  type ProfileType = B2BProfile | UserInfo;

  return useFetcher({
    enabled: isLoggedIn,
    queryKey: [url],
    queryFn: () =>
      new Promise((resolve) => {
        fetcher<ProfileType>({ url })
          .then(({ data }) => resolve(data))
          .catch(async () => {
            resolve(undefined);
          });
      })
  }) as UseQueryResult<ProfileType>;
};

export const useUserInfo = () => {
  const { isLoggedIn } = useLoginStatus();
  const { profileType } = useJwtToken();
  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;
  return useFetcher({
    enabled: isLoggedIn,
    queryKey: [url],
    queryFn: () =>
      fetcher<UserInfo>({
        url
      }).then(({ data }) => data)
  }) as UseQueryResult<UserInfo>;
};

export const useB2BProfile = () => {
  const { isLoggedIn } = useLoginStatus();
  return useFetcher({
    enabled: isLoggedIn,
    queryKey: [AUTH_ENDPOINTS.B2B_PROFILE],
    queryFn: () =>
      fetcher<B2BProfile>({
        url: AUTH_ENDPOINTS.B2B_PROFILE
      }).then(({ data }) => data)
  }) as UseQueryResult<B2BProfile>;
};

export const updateUserInfo = (data: UserInfoUpdateBody) => {
  const { profileType } = useJwtToken();
  const url =
    profileType === "B2B" ? AUTH_ENDPOINTS.B2B_PROFILE : AUTH_ENDPOINTS.ME;

  return fetcher<UserInfo>({
    url,
    method: "PATCH",
    data
  }).then(({ data }) => data);
};
